import { cn } from 'util/cn'

import { css } from 'design'
import { Button } from 'primitives/button'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { WidgetHeaderBar } from '../../components/live_chat_widget/widget_header_bar'
import { MediaRecorder } from '../../components/media_recorder/media_recorder'
import { DevicesContext } from '../../contexts/devices_context'
import { useNavToTopic } from '../../hooks/app_location/use_nav_to_topic'
import { useAxios } from '../../hooks/use_axios'
import { createNewLiveChat, sendChat } from '../../reduxes/slices/chats/actions'
import { getSelectedTopicId } from '../../reduxes/slices/selected/selectors'
import { AppLocationContext, WIDGET_VIEW } from '../admin/app_location_provider'
import { VIDEO_RATIO, WidgetContext } from './widget_page_router'

export const MediaRecorderPage = () => {
  const { appLocation, setAppLocation } = useContext(AppLocationContext)
  const { permissionStates } = useContext(DevicesContext)
  const dispatch = useDispatch()
  const axios = useAxios()
  const selectedTopicId = useSelector(getSelectedTopicId)
  const [widgetTopicId, setWidgetTopicId] = useState<number | undefined>()
  const { ready } = useContext(AppLocationContext)
  const navToTopic = useNavToTopic()
  const { goBack } = useContext(WidgetContext)

  useEffect(() => {
    if (ready && widgetTopicId) {
      navToTopic(widgetTopicId, { widgetView: WIDGET_VIEW.MESSENGER })
      setWidgetTopicId(undefined)
    }
  }, [navToTopic, ready, widgetTopicId])

  const mediaStreamConstraints = useMemo(() => {
    return {
      video:
        appLocation.widgetView === WIDGET_VIEW.VIDEO_RECORDER
          ? {
              aspectRatio: VIDEO_RATIO.width / VIDEO_RATIO.height,
            }
          : false,
      audio: true,
    }
  }, [appLocation.widgetView])

  const onSubmit = useCallback(
    (html) => {
      if (selectedTopicId) {
        return dispatch(axios(sendChat(selectedTopicId, { html }, null))).then(
          () => {
            setAppLocation({ widgetView: WIDGET_VIEW.MESSENGER })
            return Promise.resolve()
          },
        )
      } else {
        return dispatch(axios(createNewLiveChat({ html }))).then(
          (result: any) => {
            const tId = parseInt(
              Object.keys(result.data.records.topics.data)[0],
              10,
            )
            setWidgetTopicId(tId)
            return Promise.resolve()
          },
        )
      }
    },
    [axios, dispatch, selectedTopicId, setAppLocation],
  )

  const hasPermission =
    (appLocation.widgetView === WIDGET_VIEW.VIDEO_RECORDER &&
      (permissionStates.camera === 'granted' ||
        permissionStates.camera === undefined) &&
      (permissionStates.microphone === 'granted' ||
        permissionStates.microphone === undefined)) ||
    (appLocation.widgetView === WIDGET_VIEW.AUDIO_RECORDER &&
      (permissionStates.microphone === 'granted' ||
        permissionStates.microphone === undefined))

  return (
    <>
      {!hasPermission && <WidgetHeaderBar />}
      <MediaRecorder
        mediaStreamConstraints={mediaStreamConstraints}
        onSubmit={onSubmit}
        // eslint-disable-next-line
        // @ts-ignore
        backButton={<BackButton goBack={goBack} />}
        SubmitButton={SubmitButton}
        timer={120000}
      />
    </>
  )
}

const BackButton = ({ goBack }: any) => {
  return (
    <Button onClick={goBack}>
      <i className={cn('fas fa-chevron-left', iconStyles())} />
      Back
    </Button>
  )
}

const SubmitButton = ({ isUploading, isSubmitting, ...props }: any) => {
  return (
    <Button
      variant={isSubmitting || isUploading ? 'secondary' : 'primary'}
      className={submitButtonStyles()}
      {...props}
    >
      {isSubmitting ? 'Sending...' : isUploading ? 'Uploading...' : 'Send'}
    </Button>
  )
}

const submitButtonStyles = css({
  width: '100%',
  margin: '0 $4',
  padding: '$4',
  background:
    'radial-gradient(circle at 56% -384%, #05d5f8, #037be9 84%) !important',
})

const iconStyles = css({
  marginRight: '$1',
})
